export const MEDIA_PACKAGE_SCHEMA_VERSION = 'guidesofia.media_package.v1';

export const MEDIA_CHANNELS = [
  { id: 'website', label: 'Website announcement', group: 'Owned channels' },
  { id: 'instagram_facebook', label: 'Instagram and Facebook', group: 'Social' },
  { id: 'newsletter', label: 'Newsletter or launch email', group: 'Owned channels' },
  { id: 'press_release', label: 'Press release', group: 'Press' },
];

export const DEFAULT_INSTAGRAM_STORY_SCRIPT_INSTRUCTIONS = `Find the single most irresistible truthful angle in each stop. Treat "click-baity" as a strong curiosity gap, never as exaggeration or deception.

- Prefer a reversal, secret, conflict, failed ambition, absurd decision, human risk, unlikely coincidence, taboo detail, or unexpected consequence.
- Rank the stops by narrative potential and choose the strongest stories, not simply the first stops.
- Silently draft at least three possible hooks for each selected stop, then use only the one with the strongest truthful curiosity gap.
- Open with a short sentence that makes the listener need the answer. The first 8 to 12 words must create tension, surprise, or an unanswered question.
- Build one clear micro-story. Do not summarize the stop or stack unrelated facts.
- Hold back one key answer, reason, or consequence until the final lines.
- End at the most intriguing truthful moment, then invite the listener to hear what happened in the audio guide.
- Make the on-screen headline sharp, specific, and impossible to confuse with tourism advertising.
- Use Sofia's intelligent, playful edge, but keep every claim source-grounded and appropriate for client approval.
- Avoid generic praise, promotional filler, vague mystery, fake suspense, and empty phrases such as "discover the story".
- Aim for 65 to 85 spoken words. Every sentence must either increase curiosity, advance the story, or deliver the payoff.`;

const cleanText = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
const cleanMultiline = (value) => String(value ?? '').replace(/\r\n/g, '\n').trim();

const isValidPublicCampaignUrl = (value) => {
  try {
    const url = new URL(cleanText(value));
    return url.protocol === 'https:' && Boolean(url.hostname);
  } catch (error) {
    return false;
  }
};

const stripHtml = (value) => cleanText(
  String(value ?? '')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
);

const isPopupStop = (point = {}) => (
  point.point_type === 'popup_stop'
  || point.type === 'popup_stop'
  || point.stop_type === 'popup_stop'
);

const visibleStops = (itinerary = {}) => (
  (Array.isArray(itinerary.points) ? itinerary.points : [])
    .filter((point) => point && !isPopupStop(point))
);

const firstNonEmpty = (...values) => (
  values.map(cleanText).find(Boolean) || ''
);

const marker = (label) => `[[${label} REQUIRED]]`;
const present = (value, label) => cleanText(value) || marker(label);

const normalizeMediaLanguage = (value) => {
  const language = cleanText(value);
  const exactLanguages = ['en-US', 'pt-BR', 'pt-PT', 'es-ES', 'fr-FR', 'de-DE', 'it-IT', 'nl-NL', 'ru-RU', 'bg-BG'];
  if (exactLanguages.includes(language)) return language;
  const baseLanguageDefaults = {
    en: 'en-US', pt: 'pt-PT', es: 'es-ES', fr: 'fr-FR', de: 'de-DE',
    it: 'it-IT', nl: 'nl-NL', ru: 'ru-RU', bg: 'bg-BG',
  };
  return baseLanguageDefaults[language.toLowerCase().split('-')[0]] || 'en-US';
};

const hashtagToken = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '')
  .trim();

const buildDefaultHashtags = (facts) => {
  const specific = [
    facts.venue,
    facts.city,
    facts.country,
    facts.guide_title,
    ...(facts.stops || []).slice(0, 4).map((stop) => stop.name),
  ].map(hashtagToken).filter((value, index, values) => value && values.indexOf(value) === index).slice(0, 5);
  return ['#guidesofia', '#audioguide', '#culturaltourism', '#museumexperience', ...specific.map((value) => `#${value}`)].join(' ');
};

const normalizePartnerSocialHandle = (value) => {
  const handle = cleanText(value).replace(/\s+/g, '').replace(/^@+/, '');
  return handle ? `@${handle}` : '';
};

const buildSocialMentions = (partnerHandle, existing = '') => {
  const mandatory = ['@guide.sofia', normalizePartnerSocialHandle(partnerHandle)].filter(Boolean);
  const retiredPlaceholders = new Set(['@museum', '@tourismboard']);
  const extras = cleanText(existing).split(/\s+/).filter((item) => (
    item.startsWith('@') && !mandatory.includes(item) && !retiredPlaceholders.has(item)
  ));
  return [...mandatory, ...extras].join(' ');
};

const buildDefaultVisualCta = (organisation, guideTitle, language) => {
  const client = cleanText(organisation) || 'GuideSofia';
  const title = cleanText(guideTitle) || 'Audioguia';
  const baseLanguage = String(language || 'en').toLowerCase().split('-')[0];
  const labels = {
    pt: { presents: 'apresenta', audioGuide: 'Audioguia' },
    en: { presents: 'presents', audioGuide: 'Audio guide' },
    es: { presents: 'presenta', audioGuide: 'Audioguía' },
    fr: { presents: 'présente', audioGuide: 'Audioguide' },
    de: { presents: 'präsentiert', audioGuide: 'Audioguide' },
    it: { presents: 'presenta', audioGuide: 'Audioguida' },
    nl: { presents: 'presenteert', audioGuide: 'Audiogids' },
  }[baseLanguage] || { presents: 'presents', audioGuide: 'Audio guide' };
  return `${client} ${labels.presents}: ${labels.audioGuide}: ${title}`;
};

const MEDIA_LANGUAGE_LABELS = {
  'en-US': 'English',
  'pt-PT': 'Portuguese',
  'pt-BR': 'Brazilian Portuguese',
  'es-ES': 'Spanish',
  'fr-FR': 'French',
  'de-DE': 'German',
  'it-IT': 'Italian',
  'nl-NL': 'Dutch',
  'ru-RU': 'Russian',
  'bg-BG': 'Bulgarian',
};

const formatGuideLanguages = (value) => {
  const codes = Array.isArray(value)
    ? value
    : String(value || '').split(',').map((item) => cleanText(item)).filter(Boolean);
  return codes.map((code) => MEDIA_LANGUAGE_LABELS[code] || code).join(', ');
};

const joinNatural = (items = []) => {
  const values = items.map(cleanText).filter(Boolean);
  if (values.length === 0) return '';
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
};

const stopSummary = (point = {}, index = 0) => ({
  id: cleanText(point.id) || `stop-${index + 1}`,
  name: firstNonEmpty(point.name, point.title, 'Untitled stop'),
  in_depth_description: cleanMultiline(point.inDepthDescription).slice(0, 6000),
  description: firstNonEmpty(
    point.inDepthDescription,
    point.description,
    point.subtitle,
    point.historicalContext
  ).slice(0, 1200),
});

export const buildMediaGuideFacts = (itinerary = {}) => {
  const metadata = itinerary.metadata || {};
  const museumInfo = metadata.museum_info || {};
  const venuePage = metadata.venue_page || {};
  const stops = visibleStops(itinerary).map(stopSummary);

  return {
    guide_id: cleanText(metadata.id || metadata.parent_guide_id),
    whole_guide_qr_url: cleanText(metadata.parent_guide_id || metadata.id)
      ? `https://guidesofia.com/qr/${encodeURIComponent(cleanText(metadata.parent_guide_id || metadata.id))}`
      : '',
    guide_title: cleanText(metadata.name),
    guide_description: stripHtml(metadata.description),
    venue: firstNonEmpty(venuePage.displayName, venuePage.museumName, metadata.city, metadata.name),
    city: cleanText(metadata.city),
    country: cleanText(metadata.country),
    language: cleanText(metadata.language || 'en'),
    duration: cleanText(metadata.duration),
    number_of_stops: stops.length,
    thumbnail_url: firstNonEmpty(
      metadata.thumbnailUrl,
      metadata.thumbnail_url,
      metadata.coverImageUrl,
      metadata.cover_image_url,
      metadata.photoUrl,
      metadata.Photo,
      venuePage.heroImageUrl,
      venuePage.heroImage,
      itinerary.thumbnailUrl,
      itinerary.thumbnail_url
    ),
    partner_logo_url: firstNonEmpty(museumInfo.logoUrl, venuePage.logoUrl),
    website_url: firstNonEmpty(museumInfo.website, venuePage.info?.website, metadata.website),
    admission: cleanText(museumInfo.admission),
    opening_hours: cleanText(museumInfo.opening_hours),
    accessibility: cleanText(museumInfo.accessibility),
    stops,
  };
};

export const buildDefaultMediaConfig = (itinerary = {}) => {
  const facts = buildMediaGuideFacts(itinerary);
  const metadata = itinerary.metadata || {};
  const outputLanguage = normalizeMediaLanguage(facts.language);
  const selectedStopIds = facts.stops.slice(0, 3).map((stop) => stop.id).filter(Boolean);

  return {
    output_language: outputLanguage,
    organisation: facts.venue,
    venue: facts.venue,
    guide_title: facts.guide_title,
    public_guide_url: facts.whole_guide_qr_url,
    short_url: 'https://www.guidesofia.com/',
    launch_date: '',
    price: 'Free',
    start_point: facts.stops[0]?.name || '',
    access_method: 'Scan the QR code or open the guide link',
    languages: [outputLanguage],
    press_name: '',
    press_email: '',
    press_phone: '',
    client_quote: '',
    quote_attribution: '',
    technology_credit: 'Audioguides by GuideSofia',
    guidesofia_brand_fact: 'GuideSofia also offers hundreds of free audio guides across Portugal, helping people discover places through stories they can hear on their own phone.',
    content_type: 'audio_guide',
    custom_content_name: '',
    personalized_ai_enabled: Boolean(metadata.ai_generated_personalization),
    hashtags: buildDefaultHashtags(facts),
    partner_social_handle: '',
    social_mentions: '@guide.sofia',
    campaign_image_url: facts.thumbnail_url,
    campaign_image_attribution: null,
    partner_logo_url: facts.partner_logo_url,
    brand_primary_color: '#3b87a9',
    brand_accent_color: '#57bfdc',
    visual_gradient_start_color: '#020617',
    visual_gradient_end_color: '#020617',
    visual_headline: facts.guide_title,
    visual_subtitle: '',
    visual_cta: buildDefaultVisualCta(facts.venue, facts.guide_title, outputLanguage),
    visual_cta_size_percent: 100,
    image_alt_text: `Campaign image for ${facts.guide_title || facts.venue || 'the audio guide'}`,
    phone_frame_url: '',
    phone_screen_url: '',
    phone_size_percent: 72,
    phone_x_percent: 50,
    phone_y_percent: 52,
    phone_tilt_degrees: -8,
    phone_screen_x_percent: 50,
    phone_screen_y_percent: 50,
    phone_screen_width_percent: 82,
    phone_screen_height_percent: 84,
    phone_screen_zoom_percent: 100,
    phone_screen_corner_radius_percent: 5,
    phone_overlay_text: '',
    phone_overlay_text_size_percent: 4.5,
    phone_overlay_text_color: '#ffffff',
    phone_subheader_text: '',
    phone_subheader_text_size_percent: 2.5,
    phone_subheader_text_color: '#d1d5db',
    phone_overlay_logo_size_percent: 8,
    phone_partner_logo_padding_percent: 12,
    phone_background_start_color: '#020617',
    phone_background_end_color: '#374151',
    phone_partner_logo_position: { x: 91, y: 8 },
    phone_guidesofia_logo_position: { x: 91, y: 18 },
    phone_overlay_text_position: { x: 7, y: 7 },
    phone_subheader_position: { x: 7, y: 16 },
    phone_show_qr: false,
    phone_qr_size_percent: 18,
    phone_qr_position: { x: 13, y: 84 },
    phone_screen_corners: {
      top_left: { x: 9, y: 5 },
      top_right: { x: 91, y: 5 },
      bottom_right: { x: 91, y: 95 },
      bottom_left: { x: 9, y: 95 },
    },
    asset_copy: {},
    channel_instructions: {},
    promotion_calendar: [],
    visual_image_x: 50,
    visual_image_y: 50,
    visual_overlay_strength: 70,
    visual_text_position: 'bottom',
    video_duration_seconds: 15,
    ken_burns_zoom: 1.25,
    ken_burns_direction: 'dramatic_push',
    instagram_story_script_instructions: DEFAULT_INSTAGRAM_STORY_SCRIPT_INSTRUCTIONS,
    instagram_story_series: [],
    instagram_story_voice_id: '',
    instagram_story_speech_rate: '1.0',
    instagram_story_active_id: '',
    instagram_story_video_storage_path: '',
    instagram_story_video_fingerprint: '',
    instagram_story_source_storage_path: '',
    instagram_story_source_fingerprint: '',
    instagram_story_source_kind: '',
    instagram_story_last_render_url: '',
    instagram_story_last_render_storage_path: '',
    selected_stop_ids: selectedStopIds,
  };
};

const PORTUGUESE_DAYS = [
  '', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove', 'dez',
  'onze', 'doze', 'treze', 'catorze', 'quinze', 'dezasseis', 'dezassete', 'dezoito',
  'dezanove', 'vinte', 'vinte e um', 'vinte e dois', 'vinte e três', 'vinte e quatro',
  'vinte e cinco', 'vinte e seis', 'vinte e sete', 'vinte e oito', 'vinte e nove',
  'trinta', 'trinta e um',
];
const PORTUGUESE_MONTHS = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

export const formatMediaLaunchDate = (value, locale = 'en-US') => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return cleanText(value);
  const [, year, month, day] = match;
  const dayNumber = Number(day);
  const monthIndex = Number(month) - 1;
  if (String(locale).toLowerCase().startsWith('pt')) {
    return `${PORTUGUESE_DAYS[dayNumber]} de ${PORTUGUESE_MONTHS[monthIndex]} de ${year}`;
  }
  const date = new Date(Date.UTC(Number(year), monthIndex, dayNumber));
  try {
    return new Intl.DateTimeFormat(locale || 'en-US', {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    }).format(date);
  }
};

export const getSelectedMediaStops = (facts, config) => {
  const selectedIds = new Set(
    (Array.isArray(config?.selected_stop_ids) ? config.selected_stop_ids : [])
      .map(String)
  );
  return (facts?.stops || []).filter((stop) => selectedIds.has(String(stop.id)));
};

export const deriveStoryPromises = (facts, config) => {
  const selected = getSelectedMediaStops(facts, config);
  return selected.slice(0, 3).map((stop) => ({
    stop_id: stop.id,
    title: stop.name,
    hook: stop.description
      ? stop.description.split(/(?<=[.!?])\s+/)[0].slice(0, 220)
      : `Hear the story behind ${stop.name}.`,
  }));
};

const packageValues = (facts, config, storyPromises) => {
  const promises = storyPromises.map((item) => cleanText(item.title)).filter(Boolean);
  const promiseText = joinNatural(promises);

  return {
    organisation: present(config.organisation, 'ORGANISATION'),
    venue: present(config.venue, 'VENUE'),
    guideTitle: present(config.guide_title, 'GUIDE TITLE'),
    guideUrl: present(config.public_guide_url, 'PUBLIC GUIDE URL'),
    shortUrl: cleanText(config.short_url) || present(config.public_guide_url, 'PUBLIC GUIDE URL'),
    launchDate: present(formatMediaLaunchDate(config.launch_date, config.output_language), 'LAUNCH DATE'),
    price: present(config.price, 'PRICE OR FREE'),
    startPoint: present(config.start_point, 'START POINT'),
    accessMethod: present(config.access_method, 'ACCESS METHOD'),
    languages: present(formatGuideLanguages(config.languages), 'LANGUAGES'),
    duration: cleanText(config.duration),
    stopCount: facts.number_of_stops || marker('STOP COUNT'),
    description: facts.guide_description,
    promises,
    promiseText: promiseText || marker('FEATURED STORIES'),
    storyOne: promises[0] || marker('FEATURED STORY'),
    institutionalObjective: cleanText(config.institutional_objective),
    clientQuote: cleanText(config.client_quote),
    quoteAttribution: cleanText(config.quote_attribution),
    supportContact: cleanText(config.support_contact),
    pressName: cleanText(config.press_name),
    pressEmail: cleanText(config.press_email),
    pressPhone: cleanText(config.press_phone),
    technologyCredit: cleanText(config.technology_credit),
    guideSofiaBrandFact: cleanText(config.guidesofia_brand_fact),
    socialMentions: cleanText(config.social_mentions),
    hashtags: cleanText(config.hashtags),
  };
};

export const buildDeterministicMediaDrafts = (facts, config, storyPromises) => {
  const value = packageValues(facts, config, storyPromises);
  const durationPhrase = value.duration ? ` The complete route takes approximately ${value.duration}.` : '';
  const objectiveParagraph = value.institutionalObjective
    ? `\n\nThe project supports ${value.institutionalObjective}.`
    : '';
  const quoteParagraph = value.clientQuote
    ? `\n\n"${value.clientQuote}"${value.quoteAttribution ? `, said ${value.quoteAttribution}.` : ''}`
    : '';
  const technologyParagraph = value.technologyCredit
    ? `\n\n${value.technologyCredit}`
    : '';

  return {
    website: `The story behind ${value.storyOne}

What connects ${value.storyOne} to the rest of ${value.venue}? ${value.guideTitle} follows that question through ${value.stopCount} audio stories in ${value.languages}, including ${value.promiseText}.${durationPhrase}

The guide is ${value.price}. Begin at ${value.startPoint} and ${value.accessMethod.toLowerCase()}.${technologyParagraph}${value.guideSofiaBrandFact ? `

${value.guideSofiaBrandFact}` : ''}

Button: Hear the audio guide`,

    instagram_facebook: `What does ${value.storyOne} reveal about ${value.venue}?

The official audio guide follows that story alongside ${value.stopCount} stories in total, including ${value.promiseText}.

Listen in ${value.languages}, follow at your own pace, and begin by ${value.accessMethod.toLowerCase()}.

The guide is ${value.price}. Download the audio guide and start listening: ${value.shortUrl}

${value.guideSofiaBrandFact ? `${value.guideSofiaBrandFact}\n\n` : ''}${value.socialMentions ? `${value.socialMentions}\n\n` : ''}${value.hashtags || ''}`,

    short_video: `Target duration: 20 to 30 seconds

Visual: A recognisable arrival view of ${value.venue}.
Voiceover: "You may know ${value.venue}, but do you know the story behind ${value.storyOne}?"

Visual: Two or three details connected to the featured stories.
Voiceover: "The audio guide brings together ${value.stopCount} stories, including ${value.promiseText}, at your own pace."

Visual: A visitor opens the guide on a phone.
Voiceover: "Available in ${value.languages}. ${value.accessMethod}."

End card:
${value.guideTitle}
Open the audio guide`,

    newsletter: `Subject: The story behind ${value.storyOne}

Preview text: ${value.stopCount} stories that change how you experience ${value.venue}.

There is more to ${value.venue} than a quick visit reveals.

Explore ${value.promiseText}, in ${value.languages} and at your own pace.${durationPhrase}

The guide is ${value.price}. Begin at ${value.startPoint} or open it from the button below.${value.guideSofiaBrandFact ? `

${value.guideSofiaBrandFact}` : ''}

Button: Hear the audio guide`,

    press_release: `${value.organisation} and GuideSofia reveal the stories of ${value.venue}

The audio guide brings ${value.stopCount} carefully chosen stories to visitors in ${value.languages}.

Visitors to ${value.venue} can explore the place through an audio guide created by ${value.organisation} with GuideSofia.

The guide features ${value.stopCount} stops and introduces stories including ${value.promiseText}. It is available in ${value.languages} and is designed to let visitors follow the experience at their own pace.${objectiveParagraph}${quoteParagraph}

Visitors begin at ${value.startPoint} by ${value.accessMethod.toLowerCase()}. The guide is ${value.price} and can also be opened through the venue website or the on-site QR code.${technologyParagraph}${value.guideSofiaBrandFact ? `

About GuideSofia
${value.guideSofiaBrandFact}` : ''}

Media contact:
${value.pressName || marker('PRESS NAME')}
${value.pressEmail || marker('PRESS EMAIL')}
${value.pressPhone || marker('PRESS PHONE')}`,

    newspaper_notice: `${value.organisation} offers an audio guide to ${value.venue}. Available in ${value.languages}, it includes ${value.stopCount} stories, including ${value.promiseText}. Visitors can begin at ${value.startPoint} or open it through the venue website or the on-site QR code. The guide is ${value.price}.`,

    radio: `The official audio guide from ${value.organisation} brings you ${value.stopCount} stories from ${value.venue} in ${value.languages}, including ${value.storyOne}. Begin at ${value.startPoint} or open it through the venue website or the on-site QR code. ${value.price}.`,

    partner_message: `Visitors can now explore ${value.venue} using the official audio guide from ${value.organisation}. It includes ${value.stopCount} stories in ${value.languages} and is ${value.price}.

The guide begins at ${value.startPoint}. Open it through the venue website or the on-site QR code.`,

    onsite_sign: `Hear the stories of ${value.venue}

Open the official audio guide and explore ${value.stopCount} stories in ${value.languages}, at your own pace.

${value.accessMethod}

${value.price}`,

    staff_script: `The official guide has ${value.stopCount} stories in ${value.languages}. You can open it by ${value.accessMethod.toLowerCase()}, and follow it at your own pace. It is ${value.price}.${value.supportContact ? ` If you need help, contact ${value.supportContact}.` : ''}`,
  };
};

export const validateMediaPackage = (packageData) => {
  const errors = [];
  const config = packageData?.config || {};
  const required = [
    ['output_language', 'Media package language'],
    ['organisation', 'Organisation'],
    ['venue', 'Venue'],
    ['guide_title', 'Guide title'],
    ['public_guide_url', 'Public guide URL'],
    ['short_url', 'Public campaign link'],
    ['launch_date', 'Launch date'],
    ['content_type', 'Public experience name'],
    ['price', 'Price or free status'],
    ['start_point', 'Start point'],
    ['access_method', 'Access method'],
    ['languages', 'Languages'],
    ['partner_social_handle', 'Partner Instagram handle'],
  ];

  required.forEach(([key, label]) => {
    if (!cleanText(config[key])) errors.push(`${label} is missing.`);
  });
  if (cleanText(config.short_url) && !isValidPublicCampaignUrl(config.short_url)) {
    errors.push('Public campaign link must be a complete https:// URL.');
  }
  if (cleanText(config.partner_social_handle) && !/^@[a-zA-Z0-9._]+$/.test(cleanText(config.partner_social_handle))) {
    errors.push('Partner Instagram handle must start with @ and contain only letters, numbers, periods, or underscores.');
  }
  if (config.content_type === 'custom' && !cleanText(config.custom_content_name)) {
    errors.push('Custom experience name is missing.');
  }

  if (!Array.isArray(config.selected_stop_ids) || config.selected_stop_ids.length === 0) {
    errors.push('Select at least one featured guide story.');
  } else {
    const selectedIds = new Set(config.selected_stop_ids.map(String));
    const selectedWithApprovedStory = (packageData?.guide?.stops || []).filter((stop) => (
      selectedIds.has(String(stop.id)) && cleanMultiline(stop.in_depth_description)
    ));
    if (selectedWithApprovedStory.length === 0) {
      errors.push('The selected stories need an inDepthDescription before Media Package copy can be generated.');
    }
  }

  MEDIA_CHANNELS.forEach((channel) => {
    if (!cleanMultiline(packageData?.drafts?.[channel.id])) {
      errors.push(`${channel.label} copy is empty.`);
    }
  });

  if (Object.values(packageData?.drafts || {}).some((draft) => /\[\[[^\]]+ REQUIRED\]\]/.test(draft))) {
    errors.push('One or more drafts still contain required-field markers.');
  }
  if (packageData?.generation?.stale) {
    errors.push('Package inputs changed after the drafts were generated.');
  }

  return errors;
};

export const createMediaPackage = (itinerary = {}, previous = null) => {
  const facts = buildMediaGuideFacts(itinerary);
  const defaultConfig = buildDefaultMediaConfig(itinerary);
  const partnerSocialHandle = normalizePartnerSocialHandle(previous?.config?.partner_social_handle);
  const previousVisualCta = cleanText(previous?.config?.visual_cta);
  const legacyVisualCtas = new Set(['Open the guide', 'Open the audio guide', 'Scan to open the guide', 'Ouve o audioguia']);
  const config = {
    ...defaultConfig,
    ...(previous?.config || {}),
    selected_stop_ids: Array.isArray(previous?.config?.selected_stop_ids)
      ? previous.config.selected_stop_ids
      : defaultConfig.selected_stop_ids,
    public_guide_url: defaultConfig.public_guide_url || cleanText(previous?.config?.public_guide_url),
    short_url: cleanText(previous?.config?.short_url) || defaultConfig.short_url,
    personalized_ai_enabled: previous?.config?.personalized_ai_enabled ?? defaultConfig.personalized_ai_enabled,
    brand_primary_color: !previous?.config?.brand_primary_color || previous.config.brand_primary_color === '#0f172a'
      ? defaultConfig.brand_primary_color
      : previous.config.brand_primary_color,
    brand_accent_color: !previous?.config?.brand_accent_color || previous.config.brand_accent_color === '#38bdf8'
      ? defaultConfig.brand_accent_color
      : previous.config.brand_accent_color,
    campaign_image_url: cleanText(previous?.config?.campaign_image_url) || defaultConfig.campaign_image_url,
    organisation: cleanText(previous?.config?.organisation) || defaultConfig.organisation,
    price: cleanText(previous?.config?.price) || defaultConfig.price,
    technology_credit: !cleanText(previous?.config?.technology_credit)
      || previous?.config?.technology_credit === 'GuideSofia provides the audio guide technology.'
      ? defaultConfig.technology_credit
      : previous.config.technology_credit,
    hashtags: Object.prototype.hasOwnProperty.call(previous?.config || {}, 'hashtags')
      ? cleanText(previous.config.hashtags)
      : defaultConfig.hashtags,
    partner_social_handle: partnerSocialHandle,
    social_mentions: buildSocialMentions(partnerSocialHandle, previous?.config?.social_mentions),
    visual_subtitle: cleanText(previous?.config?.visual_subtitle) || defaultConfig.visual_subtitle,
    visual_cta: !previousVisualCta || legacyVisualCtas.has(previousVisualCta)
      ? defaultConfig.visual_cta
      : previousVisualCta,
    asset_copy: Object.fromEntries(Object.entries(previous?.config?.asset_copy || {}).map(([assetId, assetConfig]) => {
      const assetVisualCta = cleanText(assetConfig?.visual_cta);
      return [assetId, {
        ...(assetConfig || {}),
        ...(legacyVisualCtas.has(assetVisualCta) ? { visual_cta: defaultConfig.visual_cta } : {}),
      }];
    })),
    visual_cta_size_percent: Number(previous?.config?.visual_cta_size_percent) || defaultConfig.visual_cta_size_percent,
    visual_gradient_start_color: cleanText(previous?.config?.visual_gradient_start_color) || defaultConfig.visual_gradient_start_color,
    visual_gradient_end_color: cleanText(previous?.config?.visual_gradient_end_color) || defaultConfig.visual_gradient_end_color,
    image_alt_text: cleanText(previous?.config?.image_alt_text) || defaultConfig.image_alt_text,
    languages: Array.isArray(previous?.config?.languages)
      ? previous.config.languages
      : cleanText(previous?.config?.languages)
        ? previous.config.languages.split(',').map((item) => cleanText(item)).filter(Boolean)
        : defaultConfig.languages,
  };
  const storyPromises = Array.isArray(previous?.story_promises) && previous.story_promises.length > 0
    ? previous.story_promises
    : deriveStoryPromises(facts, config);
  const previousLanguageMatches = previous?.generation?.output_language === config.output_language;
  const drafts = previousLanguageMatches && previous?.drafts && typeof previous.drafts === 'object'
    ? MEDIA_CHANNELS.reduce((result, channel) => {
      result[channel.id] = cleanMultiline(previous.drafts[channel.id]);
      return result;
    }, {})
    : MEDIA_CHANNELS.reduce((result, channel) => ({ ...result, [channel.id]: '' }), {});
  const now = new Date().toISOString();

  return {
    schema_version: MEDIA_PACKAGE_SCHEMA_VERSION,
    status: cleanText(previous?.status || 'draft'),
    guide: facts,
    config,
    story_promises: storyPromises,
    drafts,
    generation: {
      mode: cleanText(previous?.generation?.mode || 'deterministic'),
      model: cleanText(previous?.generation?.model),
      output_language: previousLanguageMatches ? config.output_language : '',
      generated_at: cleanText(previous?.generation?.generated_at || now),
      stale: Boolean(previous?.generation?.stale),
    },
    created_at: cleanText(previous?.created_at || now),
    updated_at: now,
  };
};

export const refreshMediaPackageFromGuide = (itinerary, packageData) => {
  const current = createMediaPackage(itinerary, packageData);
  const storyPromises = deriveStoryPromises(current.guide, current.config);

  return {
    ...current,
    story_promises: storyPromises,
    drafts: current.drafts,
    generation: {
      ...current.generation,
      stale: true,
    },
    updated_at: new Date().toISOString(),
  };
};

export const mergeAiMediaDraft = (packageData, aiDraft, model) => {
  if (!aiDraft || typeof aiDraft !== 'object' || Array.isArray(aiDraft)) {
    throw new Error('AI response was not a JSON object.');
  }

  const nextPromises = Array.isArray(aiDraft.story_promises)
    ? aiDraft.story_promises.slice(0, 3).map((item, index) => ({
      stop_id: cleanText(item?.stop_id || packageData.story_promises?.[index]?.stop_id),
      title: cleanText(item?.title),
      hook: cleanText(item?.hook),
    })).filter((item) => item.title && item.hook)
    : [];

  const candidateDrafts = aiDraft.drafts;
  if (!candidateDrafts || typeof candidateDrafts !== 'object' || Array.isArray(candidateDrafts)) {
    throw new Error('AI response did not contain a drafts object.');
  }

  const drafts = {};
  MEDIA_CHANNELS.forEach((channel) => {
    const content = cleanMultiline(candidateDrafts[channel.id]);
    if (!content) {
      throw new Error(`AI response did not contain ${channel.label}.`);
    }
    drafts[channel.id] = content;
  });

  const visualCopy = aiDraft.visual_copy && typeof aiDraft.visual_copy === 'object'
    ? aiDraft.visual_copy
    : {};

  return {
    ...packageData,
    config: {
      ...packageData.config,
      visual_headline: cleanText(visualCopy.headline) || packageData.config.visual_headline,
      visual_cta: packageData.config.visual_cta,
      image_alt_text: cleanText(visualCopy.alt_text) || packageData.config.image_alt_text,
    },
    story_promises: nextPromises.length > 0 ? nextPromises : packageData.story_promises,
    drafts,
    generation: {
      mode: 'ai',
      model: cleanText(model),
      output_language: packageData.config.output_language,
      generated_at: new Date().toISOString(),
      stale: false,
    },
    updated_at: new Date().toISOString(),
  };
};

export const mergeAiMediaChannelDraft = (
  packageData,
  aiDraft,
  channelId,
  model,
  assetIds = []
) => {
  const channel = MEDIA_CHANNELS.find((item) => item.id === channelId);
  if (!channel) throw new Error('Unknown media channel.');
  const content = cleanMultiline(aiDraft?.drafts?.[channelId]);
  if (!content) throw new Error(`AI response did not contain ${channel.label}.`);

  const visualCopy = aiDraft?.visual_copy && typeof aiDraft.visual_copy === 'object'
    ? {
      visual_headline: cleanText(aiDraft.visual_copy.headline),
      image_alt_text: cleanText(aiDraft.visual_copy.alt_text),
    }
    : null;
  const nextAssetCopy = { ...(packageData.config.asset_copy || {}) };
  if (visualCopy) {
    assetIds.forEach((assetId) => {
      nextAssetCopy[assetId] = {
        ...(nextAssetCopy[assetId] || {}),
        ...visualCopy,
      };
    });
  }

  return {
    ...packageData,
    config: {
      ...packageData.config,
      asset_copy: nextAssetCopy,
    },
    drafts: {
      ...packageData.drafts,
      [channelId]: content,
    },
    generation: {
      mode: 'ai-by-format',
      model: cleanText(model),
      output_language: packageData.config.output_language,
      generated_at: new Date().toISOString(),
      stale: false,
    },
    updated_at: new Date().toISOString(),
  };
};

export const mediaPackageFilename = (packageData, extension = 'json') => {
  const source = cleanText(packageData?.config?.guide_title || 'guide')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `${source || 'guide'}_media_package.${extension}`;
};
